/**
 * Hook to get the best available image URL for an asset
 *
 * Checks if a proxied version exists in Firebase Storage and returns that,
 * otherwise falls back to the original URL.
 *
 * This helps avoid CORS issues in share card generation.
 */

import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface ProxiedImageRecord {
  originalUrl: string;
  proxiedUrl: string;
  assetType: 'token' | 'nftCollection';
  assetId: string;
  expiresAt: string;
}

const PROXIED_IMAGES_COLLECTION = 'proxied_asset_images';

/**
 * Get the best available image URL for a token
 */
export function useProxiedTokenImage(
  tokenAddress: string | undefined,
  originalImageUrl: string | undefined | null
): {
  imageUrl: string | null;
  isProxied: boolean;
  isLoading: boolean;
} {
  const [state, setState] = useState<{
    imageUrl: string | null;
    isProxied: boolean;
    isLoading: boolean;
  }>({
    imageUrl: originalImageUrl || null,
    isProxied: false,
    isLoading: !!tokenAddress,
  });

  useEffect(() => {
    if (!tokenAddress) {
      setState({
        imageUrl: originalImageUrl || null,
        isProxied: false,
        isLoading: false,
      });
      return;
    }

    const checkProxiedImage = async () => {
      try {
        if (!db) {
          setState({
            imageUrl: originalImageUrl || null,
            isProxied: false,
            isLoading: false,
          });
          return;
        }

        const normalizedAddress = tokenAddress.toLowerCase();
        const docRef = doc(db, PROXIED_IMAGES_COLLECTION, `token_${normalizedAddress}`);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data() as ProxiedImageRecord;
          const expiresAt = new Date(data.expiresAt).getTime();

          // Use proxied URL if not expired
          if (Date.now() < expiresAt && data.proxiedUrl) {
            setState({
              imageUrl: data.proxiedUrl,
              isProxied: true,
              isLoading: false,
            });
            return;
          }
        }

        // Fall back to original URL
        setState({
          imageUrl: originalImageUrl || null,
          isProxied: false,
          isLoading: false,
        });
      } catch (error) {
        console.warn('Error checking proxied image:', error);
        setState({
          imageUrl: originalImageUrl || null,
          isProxied: false,
          isLoading: false,
        });
      }
    };

    checkProxiedImage();
  }, [tokenAddress, originalImageUrl]);

  return state;
}

/**
 * Get the best available image URL for an NFT collection
 */
export function useProxiedCollectionImage(
  collectionSlug: string | undefined,
  originalImageUrl: string | undefined | null
): {
  imageUrl: string | null;
  isProxied: boolean;
  isLoading: boolean;
} {
  const [state, setState] = useState<{
    imageUrl: string | null;
    isProxied: boolean;
    isLoading: boolean;
  }>({
    imageUrl: originalImageUrl || null,
    isProxied: false,
    isLoading: !!collectionSlug,
  });

  useEffect(() => {
    if (!collectionSlug) {
      setState({
        imageUrl: originalImageUrl || null,
        isProxied: false,
        isLoading: false,
      });
      return;
    }

    const checkProxiedImage = async () => {
      try {
        if (!db) {
          setState({
            imageUrl: originalImageUrl || null,
            isProxied: false,
            isLoading: false,
          });
          return;
        }

        const docRef = doc(db, PROXIED_IMAGES_COLLECTION, `nftCollection_${collectionSlug}`);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data() as ProxiedImageRecord;
          const expiresAt = new Date(data.expiresAt).getTime();

          // Use proxied URL if not expired
          if (Date.now() < expiresAt && data.proxiedUrl) {
            setState({
              imageUrl: data.proxiedUrl,
              isProxied: true,
              isLoading: false,
            });
            return;
          }
        }

        // Fall back to original URL
        setState({
          imageUrl: originalImageUrl || null,
          isProxied: false,
          isLoading: false,
        });
      } catch (error) {
        console.warn('Error checking proxied collection image:', error);
        setState({
          imageUrl: originalImageUrl || null,
          isProxied: false,
          isLoading: false,
        });
      }
    };

    checkProxiedImage();
  }, [collectionSlug, originalImageUrl]);

  return state;
}

/**
 * Synchronous helper to get proxied image URL from a pre-fetched map
 * Use this when you need to batch-check multiple images
 */
export async function getProxiedImageUrl(
  assetType: 'token' | 'nftCollection',
  assetId: string,
  originalUrl: string | null
): Promise<string | null> {
  if (!originalUrl) return null;
  if (!db) return originalUrl;

  try {
    const docId = assetType === 'token'
      ? `token_${assetId.toLowerCase()}`
      : `nftCollection_${assetId}`;

    const docRef = doc(db, PROXIED_IMAGES_COLLECTION, docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data() as ProxiedImageRecord;
      const expiresAt = new Date(data.expiresAt).getTime();

      if (Date.now() < expiresAt && data.proxiedUrl) {
        return data.proxiedUrl;
      }
    }

    return originalUrl;
  } catch (error) {
    console.warn('Error getting proxied image URL:', error);
    return originalUrl;
  }
}
